import {
	ArchitecteMission,
	BureauEtudeMission,
	ControleurTechniqueMission,
	DesignerToSubmissionFormData,
	GeotechnicienMission,
	MaitreOeuvreMission,
} from "@dune/types";
import {
	Button,
	ControlledInput,
	ControlledSelect,
	MultipleSelector,
} from "@dune/ui";
import {
	architecteMissions,
	bureauEtudeMissions,
	controleurTechniqueMissions,
	designerTypeLabels,
	designerTypes,
	geotechnicienMissions,
	maitreOeuvreMissions,
} from "@dune/vars";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useShouldDisplayHTAmount } from "../../../../hooks/useParticipantToSubmissionForm";
import AddressDetailsContainer from "../../../brokers/constructionSites/AddressDetailsContainer";
import { ParticipantDocumentsTable } from "./DocumentsTable";
import { SirenInput } from "./SirenInput";
import { ControlledPriceInput } from "../../../../components/inputs/ControlledPriceInput";

interface DesignerToSubmissionFormProps {
	constructionSiteId: number;
	onSubmit: () => void;
	onCancel: () => void;
	isSubmitting?: boolean;
	documentsSlot?: React.ReactNode;
}

export const DesignerToSubmissionForm = ({
	constructionSiteId,
	onSubmit,
	onCancel,
	isSubmitting = false,
	documentsSlot,
}: DesignerToSubmissionFormProps) => {
	const {
		control,
		watch,
		setValue,
		formState: { errors },
	} = useFormContext<DesignerToSubmissionFormData>();

	const designerType = watch("designerType");

	const [establishmentType, setEstablishmentType] = useState<
		"french" | "foreign"
	>("french");

	const { shouldDisplayHT } = useShouldDisplayHTAmount(constructionSiteId);

	const getMissionsOptions = () => {
		switch (designerType) {
			case "ARCHITECTE":
				return architecteMissions.map((mission) => ({
					value: mission,
					label: mission,
				}));
			case "BUREAU_ETUDE":
				return bureauEtudeMissions.map((mission) => ({
					value: mission,
					label: mission,
				}));
			case "CONTROLEUR_TECHNIQUE":
				return controleurTechniqueMissions.map((mission) => ({
					value: mission,
					label: mission,
				}));
			case "GEOTECHNICIEN":
				return geotechnicienMissions.map((mission) => ({
					value: mission,
					label: mission,
				}));
			case "MAITRE_OEUVRE":
				return maitreOeuvreMissions.map((mission) => ({
					value: mission,
					label: mission,
				}));
			default:
				return [];
		}
	};

	const getCurrentMissions = () => {
		switch (designerType) {
			case "ARCHITECTE":
				return watch("architecteMissions") || [];
			case "BUREAU_ETUDE":
				return watch("bureauEtudeMissions") || [];
			case "CONTROLEUR_TECHNIQUE":
				return watch("controleurTechniqueMissions") || [];
			case "GEOTECHNICIEN":
				return watch("geotechnicienMissions") || [];
			case "MAITRE_OEUVRE":
				return watch("maitreOeuvreMissions") || [];
			default:
				return [];
		}
	};

	const setCurrentMissions = (missions: string[]) => {
		switch (designerType) {
			case "ARCHITECTE":
				setValue("architecteMissions", missions as ArchitecteMission[]);
				break;
			case "BUREAU_ETUDE":
				setValue("bureauEtudeMissions", missions as BureauEtudeMission[]);
				break;
			case "CONTROLEUR_TECHNIQUE":
				setValue(
					"controleurTechniqueMissions",
					missions as ControleurTechniqueMission[],
				);
				break;
			case "GEOTECHNICIEN":
				setValue("geotechnicienMissions", missions as GeotechnicienMission[]);
				break;
			case "MAITRE_OEUVRE":
				setValue("maitreOeuvreMissions", missions as MaitreOeuvreMission[]);
				break;
		}
	};

	return (
		<div className="space-y-6">
			<div className="space-y-4">
				<ControlledSelect
					name="designerType"
					control={control}
					label="Type"
					placeholder="Sélectionner"
					options={designerTypes.map((type) => {
						return {
							value: type,
							label: designerTypeLabels[type],
						};
					})}
					error={errors.designerType?.message}
					required
				/>

				{documentsSlot ?? (
					<ParticipantDocumentsTable
						constructionSiteId={constructionSiteId}
						participantType="DESIGNER"
					/>
				)}

				{designerType && (
					<MultipleSelector
						options={getMissionsOptions()}
						placeholder="Sélectionner"
						value={getCurrentMissions().map((mission) => ({
							value: mission,
							label: mission,
						}))}
						label="Missions"
						error={errors.missions?.message}
						onChange={(selectedOptions) => {
							const missions = selectedOptions.map((option) => option.value);
							setCurrentMissions(missions);
						}}
					/>
				)}

				<div className="space-y-4">
					<h3 className="text-lg font-medium">Établi en France</h3>
					<div className="flex space-x-4">
						<label className="flex items-center">
							<input
								type="radio"
								name="establishmentType"
								value="french"
								checked={establishmentType === "french"}
								onChange={(e) =>
									setEstablishmentType(e.target.value as "french")
								}
								className="mr-2"
							/>
							Oui
						</label>
						<label className="flex items-center">
							<input
								type="radio"
								name="establishmentType"
								value="foreign"
								checked={establishmentType === "foreign"}
								onChange={(e) =>
									setEstablishmentType(e.target.value as "foreign")
								}
								className="mr-2"
							/>
							Non
						</label>
					</div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
					{establishmentType === "french" ? (
						<SirenInput
							name="siren"
							label="SIREN"
							placeholder="Numéro SIREN (9 chiffres)"
						/>
					) : (
						<>
							<ControlledInput
								name="registrationNumber"
								control={control}
								label="Numéro d'enregistrement"
								placeholder="Numéro d'enregistrement"
								error={errors.registrationNumber?.message}
							/>
							<ControlledInput
								name="country"
								control={control}
								label="Pays"
								placeholder="Pays"
								error={errors.country?.message}
							/>
						</>
					)}

					<ControlledInput
						name="denomination"
						control={control}
						label="Dénomination"
						placeholder="Dénomination de l'entreprise"
						error={errors.denomination?.message}
						required
					/>
				</div>

				<div className="space-y-4">
					<h3 className="text-lg font-medium">Adresse</h3>
					<AddressDetailsContainer />
				</div>

				<div className="space-y-4">
					{shouldDisplayHT ? (
						<ControlledPriceInput
							name="totalFeesHT"
							control={control}
							label="Montant total HT"
							iconOrText="€ HT"
							required
						/>
					) : (
						<ControlledPriceInput
							name="totalFeesTTC"
							control={control}
							label="Montant total TTC"
							iconOrText="€ TTC"
							required
						/>
					)}
				</div>
			</div>

			<div className="flex justify-end space-x-3">
				<Button
					type="button"
					variant="outline"
					onClick={onCancel}
					disabled={isSubmitting}
				>
					Annuler
				</Button>
				<Button type="submit" onClick={onSubmit} disabled={isSubmitting}>
					{isSubmitting ? "Enregistrement..." : "Enregistrer"}
				</Button>
			</div>
		</div>
	);
};
